import { useMemo, useState } from "react";

import { roomLabel, type Room } from "../../shared/api/rooms";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type RoomFilter = "all" | "limited" | "unlimited";

/** So khớp không phân biệt hoa thường và dấu: "noi" tìm ra "Nội". */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();
}

/** Ô có một số ≥ 1 hợp lệ — ô trống / 0 / chữ là "không giới hạn". */
export function isLimited(raw: string | undefined): boolean {
  const n = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(n) && n >= 1;
}

/**
 * #19936 — sức chứa riêng của từng phòng (lượt/ngày). Một ô tick: bỏ tick = không phòng nào bị
 * giới hạn, tick = hiện danh sách phòng để khai. Phòng khám có thể có rất nhiều phòng, nên danh
 * sách có ô tìm, bộ lọc và khung cuộn thay vì trải hết ra trang. Trạng thái (tick + các ô) nằm ở
 * trang cha để một nút Lưu ghi tất cả.
 */
export function RoomLimitsCard({
  rooms,
  loading,
  enabled,
  onEnabledChange,
  caps,
  onCapChange,
  slotMode,
  errors = {},
  summary = null,
}: {
  rooms: Room[];
  loading: boolean;
  enabled: boolean;
  onEnabledChange: (on: boolean) => void;
  caps: Record<string, string>;
  onCapChange: (roomId: string, value: string) => void;
  slotMode: boolean;
  /** #19957 — roomId → lời báo vượt giới hạn toàn phòng khám. */
  errors?: Record<string, string>;
  /** #19957 — dòng tổng (chế độ tổng/ngày): "Tổng giới hạn các phòng: 60 / 50". */
  summary?: { text: string; error: boolean } | null;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<RoomFilter>("all");

  const limitedCount = rooms.filter((r) => isLimited(caps[r.id])).length;
  const shown = useMemo(() => {
    const q = fold(query.trim());
    return rooms.filter((r) => {
      if (q && !fold(roomLabel(r)).includes(q)) return false;
      if (filter === "limited") return isLimited(caps[r.id]);
      if (filter === "unlimited") return !isLimited(caps[r.id]);
      return true;
    });
  }, [rooms, caps, query, filter]);

  return (
    <Card data-testid="room-limits-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Giới hạn theo phòng</CardTitle>
        <p className="text-sm text-muted-foreground">
          Số lượt tối đa mỗi phòng nhận trong một ngày, cộng dồn mọi buổi. Áp dụng cả khi chọn
          "Không giới hạn" ở trên; giới hạn nào chặt hơn thì áp giới hạn đó. Lịch chưa có phòng không
          tính vào đây.
        </p>
      </CardHeader>
      <CardContent className="grid gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>Giới hạn số lượt theo phòng</span>
        </label>

        {enabled && (
          <div className="grid gap-2 pl-6">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="search"
                placeholder="Tìm phòng theo mã hoặc tên…"
                aria-label="Tìm phòng"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-8 w-full sm:w-64"
              />
              <select
                aria-label="Lọc phòng"
                value={filter}
                onChange={(e) => setFilter(e.target.value as RoomFilter)}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="all">Tất cả phòng</option>
                <option value="limited">Đã đặt giới hạn</option>
                <option value="unlimited">Chưa đặt giới hạn</option>
              </select>
              <span className="text-xs text-muted-foreground" data-testid="room-limits-summary">
                {limitedCount}/{rooms.length} phòng đang giới hạn
              </span>
            </div>

            {loading && <small className="text-muted-foreground">Đang tải danh sách phòng…</small>}
            {!loading && rooms.length === 0 && (
              <small className="text-muted-foreground">
                Chi nhánh chưa có phòng nào đang hoạt động.
              </small>
            )}
            {!loading && rooms.length > 0 && shown.length === 0 && (
              <small className="text-muted-foreground">Không có phòng nào khớp.</small>
            )}

            {shown.length > 0 && (
              <ul className="m-0 grid max-h-80 list-none gap-1 overflow-y-auto rounded-md border p-2">
                {shown.map((r) => (
                  <li key={r.id}>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="min-w-0 flex-1 truncate" title={roomLabel(r)}>
                        {roomLabel(r)}
                      </span>
                      <Input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        placeholder="Không giới hạn"
                        aria-label={`Sức chứa ${roomLabel(r)}`}
                        value={caps[r.id] ?? ""}
                        onChange={(e) => onCapChange(r.id, e.target.value)}
                        className="h-8"
                        style={{ width: 140 }}
                      />
                      <span className="text-muted-foreground">lượt/ngày</span>
                    </label>
                    {errors[r.id] && (
                      <p
                        role="alert"
                        data-testid="room-limit-error"
                        className="m-0 text-xs text-destructive"
                      >
                        {errors[r.id]}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="m-0 text-xs text-muted-foreground">Để trống = phòng không giới hạn.</p>
            {summary && (
              <p
                data-testid="room-limits-total"
                role={summary.error ? "alert" : undefined}
                className={`m-0 text-xs ${summary.error ? "text-destructive" : "text-muted-foreground"}`}
              >
                {summary.text}
              </p>
            )}
          </div>
        )}

        {slotMode && (
          <p className="m-0 text-xs text-muted-foreground">
            Hình thức Theo giờ: phòng chưa khai sức chứa thì mỗi bác sĩ nhận tối đa 1 bệnh nhân
            trong một khung giờ.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
